import {Tooltip} from 'antd';
import React, {
    useRef, FC, ChangeEvent, MouseEvent,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {DynamicIcon} from 'components/dynamic-icon';
import {showConfirmModal} from 'components/modal/confirmation-modal/confirmation-modal';
import {ATTORNEY_UPLOAD_EXTENSIONS} from 'components/table/table-constants';
import {IColumnFormatterProps} from 'components/table/table-types';
import {getAllowedActions} from 'components/table/utils/table.utils';
import {
    IFieldAction, EFieldActionType, EFieldType, selectMetadata,
} from 'entities/metadata';
import {SignWithCryptoProModal} from 'features/sign-with-crypto-pro';
import {TFileList, resetLoadedData} from 'modules/data';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages} from 'shared/constants/messages';
import {EDocStatusCodes} from 'shared/types/files';
import {
    downloadFile,
    showMessage,
    showMessageFromResponse,
} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {
    EDocumentsSigningMethod,
    selectDocumentExtensions,
    selectDocumentsSigningMethod,
} from 'store/slices/documents-uploading-slice';

import {
    attachUpload,
    attorneyUpload,
    deleteFile,
    generateAttachmentLink,
    generateAttorneyLink,
    generateSignatureLink,
    signatureUpload,
} from './file-column-utils';

import './file-column.less';

interface IRowFileProps extends IColumnFormatterProps {
    type: EFieldType;
    fieldType: string;
    entityName: string;
    entityType: EEntityType | undefined;
    actions: IFieldAction[] | undefined;
}

enum ETypeFile {
    FILE = 'file',
    SIGNATURE = 'signature',
    ATTORNEY = 'attorney',
}

export const FileColumn: FC<IRowFileProps> = ({
    fieldType, record, type, entityName, entityType, actions,
}: IRowFileProps) => {
    const allowedActions = getAllowedActions(actions);
    const fileList = record.fileList as TFileList;
    const refSignature = useRef<HTMLInputElement>(null);
    const refAttorney = useRef<HTMLInputElement>(null);
    const refAttach = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

    const acceptSign = useSelector(selectDocumentExtensions)?.accept;
    const tableMeta = useAppSelector(selectMetadata(entityName, EEntityType.TABLE));
    const documentsSigningMethod = useAppSelector(selectDocumentsSigningMethod);

    const isSigningAllowed = !!tableMeta?.actions?.map(action => action.actionCode).includes('SIGN_FILE_DOC');

    if (fileList && fileList.length) {
        const file = fileList[0];
        const fileName = type === EFieldType.SIGNATURE ? file.signatureFileName : file.fileName;
        const fileSize = type === EFieldType.FILE ? file.outputFileSize : '';
        const link = generateAttachmentLink({attachmentId: file.attachmentId, entityName});
        const signatureLink = generateSignatureLink({attachmentId: file.attachmentId, entityName});
        const attorneyLink = generateAttorneyLink({attachmentId: file.attachmentId, entityName});

        const handleFileDownload = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            downloadFile(link, fileName).then(data => {
                if (data.status === 200) {
                    showMessageFromResponse({
                        response: data,
                        customMessage: ECommonMessages.DOWNLOAD_FILE_SUCCESS,
                    });
                }
            }).catch(error => {
                showMessageFromResponse({response: error?.response, isError: true});
            });
        };

        const handleSignatureDownload = async (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (allowedActions.includes(EFieldActionType.DOWNLOAD)) {
                downloadFile(signatureLink, fileName).then(data => {
                    if (data.status === 200) {
                        showMessageFromResponse({
                            response: data,
                            customMessage: 'Подпись скачана',
                        });
                    }
                }).catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
            } else {
                showMessage({message: 'Функция выгрузки недоступна', isError: true});
            }
        };

        const handleAttorneyDownload = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            if (!allowedActions.includes(EFieldActionType.DOWNLOAD)) {
                showMessage({message: 'Функция выгрузки недоступна', isError: true});
                return;
            }

            downloadFile(attorneyLink, file.loaSignFileName).then(data => {
                if (data.status === 200) {
                    showMessageFromResponse({
                        response: data,
                        customMessage: 'Доверенность скачана',
                    });
                }
            }).catch(error => {
                if (error.response.status === 404) {
                    showMessageFromResponse({
                        response: error,
                        isError: true,
                        customMessage: 'Не удалось найти файл доверенности для скачивания, попробуйте позже',
                    });
                } else {
                    showMessageFromResponse({response: error?.response, isError: true});
                }
            });
        };

        const handleDeleteAttach = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            showConfirmModal({
                title: 'Вы действительно хотите удалить файл?',
                onSubmit: () => {
                    deleteFile(link).then(data => {
                        if (data.status === 200) {
                            dispatch(resetLoadedData(entityName, entityType));
                            showMessageFromResponse({
                                response: data,
                                customMessage: ECommonMessages.FILE_DELETED,
                            });
                        }
                    }).catch(error => {
                        showMessageFromResponse({
                            response: error?.response,
                            isError: true,
                        });
                    });
                },
                okText: 'Удалить',
            });
        };

        const handleDeleteSignature = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            showConfirmModal({
                title: 'Вы действительно хотите удалить подпись?',
                onSubmit: () => {
                    deleteFile(signatureLink).then(data => {
                        if (data.status === 200) {
                            dispatch(resetLoadedData(entityName, entityType));
                            showMessageFromResponse({
                                response: data,
                                customMessage: 'Подпись удалена',
                            });
                        }
                    }).catch(error => {
                        showMessageFromResponse({response: error?.response, isError: true});
                    });
                },
                okText: 'Удалить',
            });
        };

        const handleDeleteAttorney = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            showConfirmModal({
                title: 'Вы действительно хотите удалить доверенность?',
                onSubmit: () => {
                    deleteFile(attorneyLink).then(data => {
                        if (data.status === 200) {
                            dispatch(resetLoadedData(entityName, entityType));
                            showMessageFromResponse({
                                response: data,
                                customMessage: 'Доверенность удалена',
                            });
                        }
                    }).catch(error => {
                        if (error.response.status === 405) {
                            showMessageFromResponse({
                                response: error,
                                isError: true,
                                customMessage: 'Удалить файл доверенности не удалось, попробуйте позже',
                            });
                        } else {
                            showMessageFromResponse({
                                response: error?.response,
                                isError: true,
                            });
                        }
                    });
                },
                okText: 'Удалить',
            });
        };

        const handleSignatureUpload = (e: ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files?.[0];
            if (files) {
                signatureUpload(files, file.attachmentId).then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, entityType));
                        showMessageFromResponse({
                            response: data,
                            customMessage: 'Подпись загружена',
                        });
                    }
                }).catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
            }
        };

        const handleAttorneyUpload = (e: ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files?.[0];
            if (files) {
                attorneyUpload(files, file.attachmentId).then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, entityType));
                        showMessageFromResponse({
                            response: data,
                            customMessage: 'Доверенность загружена',
                        });
                    }
                }).catch(error => {
                    if (error.response.status === 404) {
                        showMessageFromResponse({
                            response: error,
                            isError: true,
                            customMessage: 'Не удалось загрузить файл '
                        + 'доверенности, попробуйте позже',
                        });
                    } else {
                        showMessageFromResponse({response: error?.response, isError: true});
                    }
                });
            }
        };

        if (file.fileName && fieldType === ETypeFile.FILE) {
            return (
                <div className="file-column file">
                    <span className="file-column__title">{fileSize}</span>
                    <div className="line-break" />
                    <div className="file-column__icons">
                        {allowedActions.includes(EFieldActionType.DELETE)
                            && (
                                <DynamicIcon
                                    className="file-column__icon"
                                    type="CloseOutlined"
                                    onClick={handleDeleteAttach}
                                />
                            )}
                        {allowedActions.includes(EFieldActionType.DOWNLOAD)
                            && (
                                <Tooltip title={fileName}>
                                    <DynamicIcon
                                        className="file-column__icon"
                                        type="LinkOutlined"
                                        onClick={handleFileDownload}
                                    />
                                </Tooltip>
                            )}
                        <Tooltip title={file.fileHashCode}>
                            <DynamicIcon
                                className="file-column__icon"
                                type="NumberOutlined"
                            />
                        </Tooltip>
                    </div>
                </div>
            );
        }
        if (file.signatureFileName && fieldType === ETypeFile.SIGNATURE) {
            return (
                <div className="file-column signature">
                    {allowedActions.includes(EFieldActionType.DELETE)
                        && record?.docStatusCode !== EDocStatusCodes.PUBLISHED
                        && (
                            <DynamicIcon
                                className="file-column__icon"
                                type="CloseOutlined"
                                onClick={handleDeleteSignature}
                            />
                        )}
                    {allowedActions.includes(EFieldActionType.DOWNLOAD)
                        && (
                            <Tooltip title={fileName}>
                                <DynamicIcon
                                    className="file-column__icon"
                                    type="LinkOutlined"
                                    onClick={handleSignatureDownload}
                                />
                            </Tooltip>
                        )}
                </div>
            );
        }
        if (file.loaSignFileName && fieldType === ETypeFile.ATTORNEY) {
            return (
                <div className="file-column signature">
                    {allowedActions.includes(EFieldActionType.DELETE)
                        && record?.docStatusCode !== EDocStatusCodes.PUBLISHED
                        && (
                            <DynamicIcon
                                className="file-column__icon"
                                type="CloseOutlined"
                                onClick={handleDeleteAttorney}
                            />
                        )}
                    {allowedActions.includes(EFieldActionType.DOWNLOAD)
                        && (
                            <Tooltip title={file.loaSignFileName}>
                                <DynamicIcon
                                    className="file-column__icon"
                                    type="LinkOutlined"
                                    onClick={handleAttorneyDownload}
                                />
                            </Tooltip>
                        )}
                </div>
            );
        }

        if (fieldType === ETypeFile.SIGNATURE
            && documentsSigningMethod === EDocumentsSigningMethod.CRYPTO_PRO_PLUGIN && isSigningAllowed) {
            const attachmentIds = Array.isArray(fileList) && fileList.length > 0 && 'attachmentId' in fileList[0]
                ? [fileList[0].attachmentId.toString()]
                : [];

            return (
                <SignWithCryptoProModal
                    documentIds={record?.id ? [record.id as string] : []}
                    attachmentIds={attachmentIds}
                    onFinish={() => {
                        dispatch(resetLoadedData(entityName, entityType));
                    }}
                    entityName={entityName}
                />
            );
        }

        if (fieldType === ETypeFile.SIGNATURE) {
            return (
                <div
                    className="file-column file-column__empty"
                    onClick={() => refSignature?.current?.click()}
                >
                    {allowedActions.includes(EFieldActionType.UPLOAD) && (
                        <>
                            <DynamicIcon
                                className="file-column__icon empty"
                                type="download"
                            />
                            <span>Загрузить</span>
                            <input
                                ref={refSignature}
                                id="input"
                                type="file"
                                onChange={handleSignatureUpload}
                                className="file-column__input"
                                accept={acceptSign}
                            />
                        </>
                    )}
                </div>
            );
        }
        if (fieldType === ETypeFile.ATTORNEY) {
            return (
                <div
                    className="file-column file-column__empty"
                    onClick={() => refAttorney?.current?.click()}
                >
                    {allowedActions.includes(EFieldActionType.UPLOAD) && (
                        <>
                            <DynamicIcon
                                className="file-column__icon empty"
                                type="download"
                            />
                            <span>Загрузить</span>
                            <input
                                ref={refAttorney}
                                id="input"
                                type="file"
                                onChange={handleAttorneyUpload}
                                className="file-column__input"
                                accept={ATTORNEY_UPLOAD_EXTENSIONS}
                            />
                        </>
                    )}
                </div>
            );
        }
    }
    const onAttachUpload = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files?.[0];
        if (files) {
            attachUpload(files, record.id as number)
                .then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, entityType));
                        showMessageFromResponse({response: data});
                    }
                }).catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
        }
    };
    if (fieldType === ETypeFile.FILE) {
        return (
            <div
                className="file-column file-column__empty"
                onClick={() => refAttach?.current?.click()}
            >
                {allowedActions.includes(EFieldActionType.UPLOAD) && (
                    <>
                        <DynamicIcon
                            className="file-column__icon empty"
                            type="UploadOutlined"

                        />
                        <span>Отсутствует</span>
                        <input
                            ref={refAttach}
                            id="input"
                            type="file"
                            onChange={onAttachUpload}
                            className="file-column__input"
                        />
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="file-column file-column__empty">
            {/* Значение подписи когда файл отсутствует */}
        </div>
    );
};
